import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/app/AuthContext";
import { JourneyTransferPrompt } from "@/app/JourneyTransferPrompt";
import { LayoutGrid, Compass, FileText, Wallet, User, LogOut, GraduationCap, Building2, Handshake, Shield, FolderLock, Award, ClipboardList } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { Seo } from "@/components/site/Seo";

const NAV = {
  individual: [
    { to: "/app", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/app/discover", label: "Discover", icon: Compass },
    { to: "/app/applications", label: "Applications", icon: FileText },
    { to: "/app/funding", label: "Funding", icon: Wallet },
    { to: "/app/documents", label: "Documents", icon: FolderLock },
    { to: "/app/affiliate", label: "Affiliate", icon: Handshake },
    { to: "/app/profile", label: "Profile", icon: User },
  ],
  provider: [{ to: "/app", label: "Dashboard", icon: LayoutGrid, end: true }],
  partnership: [
    { to: "/app", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/app/partner/applications", label: "Applications", icon: FileText },
    { to: "/app/partner/commissions", label: "Commissions", icon: Wallet },
  ],
  admin: [
    { to: "/app", label: "Dashboard", icon: LayoutGrid, end: true },
    { to: "/app/admin/partners", label: "Partners", icon: Handshake },
    { to: "/app/partner/applications", label: "Applications", icon: FileText },
    { to: "/app/admin/commissions", label: "Commissions", icon: Wallet },
    { to: "/app/admin/affiliates", label: "Affiliates", icon: GraduationCap },
    { to: "/app/admin/entitlements", label: "MyAE", icon: Award },
    { to: "/app/admin/reporting", label: "Reporting", icon: ClipboardList },
    { to: "/app/admin/call-requests", label: "Call requests", icon: FileText },
  ],
};

const ROLE_ICON = { individual: GraduationCap, provider: Building2, partnership: Handshake, admin: Shield };

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user.role] || NAV.individual;
  const RoleIcon = ROLE_ICON[user.role] || GraduationCap;
  const initials = user.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = () => { logout(); navigate("/app/login"); };

  return (
    <div className="min-h-screen bg-surface">
      <Seo noindex title="OpenDegree Platform" description="OpenDegree platform workspace." path="/app" />
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex items-center gap-2.5 px-6 h-[72px] border-b border-slate-100">
          <Logo markClass="h-9 w-9" textClass="text-lg" />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[15px] font-medium transition-colors duration-200 ${isActive ? "bg-cobalt/10 text-cobalt" : "text-slatey hover:bg-slate-50 hover:text-ink"}`}>
              <it.icon className="h-5 w-5" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-4">
          <LanguageSelector testId="language-selector-platform" />
          <div className="mt-3 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cobalt/10 text-cobalt text-xs font-bold">{initials}</div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
              <p className="truncate text-xs text-slatey capitalize">{user.role}</p>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="rounded-lg p-2 text-slatey hover:bg-slate-50 hover:text-ink" title="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-5 lg:hidden">
        <div className="flex items-center gap-2">
          <Logo markClass="h-8 w-8" textClass="text-base" />
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector compact testId="language-selector-platform-mobile" />
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cobalt/10 text-cobalt text-xs font-bold">{initials}</span>
          <button onClick={doLogout} className="text-slatey"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      {/* Content */}
      <main className="lg:pl-64 pb-24 lg:pb-10">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
          <div className="mb-4 flex items-center gap-2 text-xs font-medium text-slatey lg:hidden">
            <RoleIcon className="h-3.5 w-3.5 text-cobalt" /> <span className="capitalize">{user.role} workspace</span>
          </div>
          <Outlet />
        </div>
      </main>

      <JourneyTransferPrompt />

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-40 grid border-t border-slate-200 bg-white lg:hidden" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0,1fr))` }}>
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={`bottomnav-${it.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium ${isActive ? "text-cobalt" : "text-slatey"}`}>
            <it.icon className="h-5 w-5" /> {it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
